import notificationService from "../services/notification.service";
import {
  NOTIFICATION_COUNT,
  NOTIFICATION_ERROR,
  NOTIFICATION_LIST,
  NOTIFICATION_LOADING,
  NOTIFICATION_READ,
  NOTIFICATION_READ_ROLLBACK,
} from "./type";

export const NOTIFICATION_TYPES = {
  CLIENT: 1,
  INQUIRY: 2,
  CONTRACT: 3,
  ORDER: 5,
  VENDOR: 6,
};

const getResponseStatus = (response) =>
  response?.status ?? response?.response?.status ?? 0;

const getResponseMessage = (response) =>
  response?.data?.message ||
  response?.data?.data?.message ||
  response?.response?.data?.message ||
  response?.response?.data?.data?.message ||
  "";

const getFirstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getId = (value) => {
  if (!value || typeof value !== "object") return value;
  return getFirstDefined(
    value.viewId,
    value.view_id,
    value.referenceId,
    value.reference_id,
    value.inquiryId,
    value.orderId,
    value.viewID,
    value.id,
    value._id,
  );
};

const getNotificationItems = (response) => {
  const responseBody = response?.data || {};
  const data = responseBody?.data ?? responseBody;
  const containers = [data, data?.data, responseBody];
  const collectionKeys = [
    "notifications",
    "notification",
    "notificationList",
    "notificationData",
    "items",
    "results",
    "list",
    "docs",
    "rows",
    "records",
    "content",
    "data",
  ];

  for (const container of containers) {
    if (Array.isArray(container)) return container;

    for (const key of collectionKeys) {
      const value = container?.[key];
      if (Array.isArray(value)) return value;
      if (Array.isArray(value?.data)) return value.data;
    }
  }

  return [];
};

const getNotificationPagination = (response, page, limit, itemCount) => {
  const responseBody = response?.data || {};
  const data = responseBody?.data ?? responseBody;
  const pagination =
    data?.pagination ||
    data?.meta ||
    data?.data?.pagination ||
    data?.data?.meta ||
    responseBody?.pagination ||
    responseBody?.meta ||
    {};
  const total = Number(
    pagination.total ??
      pagination.totalCount ??
      pagination.totalDocs ??
      data?.total ??
      data?.totalCount ??
      data?.totalDocs ??
      data?.totalRecords ??
      responseBody?.total ??
      responseBody?.totalCount ??
      responseBody?.totalRecords ??
      itemCount,
  );
  const pageSize =
    Number(
      pagination.limit ??
        pagination.pageSize ??
        pagination.perPage ??
        data?.limit ??
        limit,
    ) || Number(limit);
  const currentPage =
    Number(pagination.page ?? pagination.currentPage ?? data?.page) ||
    Number(page);

  return {
    ...pagination,
    page: currentPage,
    limit: pageSize,
    total,
    totalPages:
      Number(
        pagination.totalPages ??
          pagination.pages ??
          pagination.total_pages ??
          data?.totalPages,
      ) || Math.max(Math.ceil(total / pageSize), 1),
  };
};

const normalizeNotification = (item = {}, index) => {
  const related =
    item.related || item.reference || item.entity || item.data || {};
  const readValue = getFirstDefined(
    item.read,
    item.isRead,
    item.readStatus,
    item.is_read,
  );
  const read =
    typeof readValue === "string"
      ? !["unread", "false", "0"].includes(readValue.toLowerCase())
      : readValue !== undefined
        ? Boolean(readValue)
        : String(item.status || "").toLowerCase() === "read";

  return {
    ...item,
    id: String(
      getFirstDefined(
        item.id,
        item._id,
        item.notificationId,
        item.notification_id,
        `notification-${index}`,
      ),
    ),
    title: getFirstDefined(
      item.title,
      item.subject,
      item.heading,
      item.notificationTitle,
      item.notification?.title,
      item.name,
      related.title,
      "Notification",
    ),
    message: getFirstDefined(
      item.message,
      item.description,
      item.content,
      item.body,
      item.text,
      item.notification?.message,
      related.message,
      "",
    ),
    time: getFirstDefined(
      item.time,
      item.createdAt,
      item.updatedAt,
      item.created,
      item.date,
      "",
    ),
    read,
    type: getFirstDefined(
      item.notificationType,
      item.notification_type,
      item.type,
      related.type,
    ),
    viewId: getId(
      getFirstDefined(
        item.viewId,
        item.view_id,
        item.viewID,
        item.view,
        related.viewId,
        related.view_id,
        related.view,
        related,
        item.data?.viewId,
        item.data?.view_id,
        item.data?.view,
        item.data?.related,
      ),
    ),
    inquiryId: getId(
      getFirstDefined(
        item.inquiryId,
        item.inquiry,
        item.inquiry_id,
        related.inquiryId,
        related.inquiry,
        item.data?.inquiryId,
        item.data?.inquiry,
      ),
    ),
    orderId: getId(
      getFirstDefined(
        item.orderId,
        item.order,
        item.order_id,
        related.orderId,
        related.order,
        item.data?.orderId,
        item.data?.order,
      ),
    ),
    referenceId: getId(
      getFirstDefined(
        item.referenceId,
        item.reference_id,
        item.refId,
        item.ref_id,
        item.relatedId,
        item.related_id,
        related.referenceId,
        related.reference_id,
        related.refId,
        related.ref_id,
        related.id,
        related._id,
        item.data?.referenceId,
        item.data?.reference_id,
        item.data?.refId,
        item.data?.ref_id,
      ),
    ),
    entityId: getId(
      getFirstDefined(item.entityId, item.entity_id, related.entityId),
    ),
  };
};

export const getNotificationListAction =
  ({ page = 1, limit = 10, type, status } = {}) =>
  async (dispatch) => {
    dispatch({ type: NOTIFICATION_LOADING, payload: true });
    try {
      const response = await notificationService.getNotifications({
        page,
        limit,
        type,
        status,
      });
      const items = getNotificationItems(response).map(normalizeNotification);
      const responseBody = response?.data || {};
      const data = responseBody?.data ?? responseBody;
      const unreadCountValue = getFirstDefined(
        data?.unreadCount,
        data?.unreadNotificationCount,
        data?.unread,
        data?.unreadNotifications,
        responseBody?.unreadCount,
        responseBody?.unreadNotificationCount,
      );

      if (getResponseStatus(response) >= 200 && getResponseStatus(response) < 300) {
        dispatch({
          type: NOTIFICATION_LIST,
          payload: {
            items,
            ...(unreadCountValue !== undefined
              ? { unreadCount: Number(unreadCountValue) || 0 }
              : {}),
            pagination: getNotificationPagination(
              response,
              page,
              limit,
              items.length,
            ),
          },
        });
        dispatch({ type: NOTIFICATION_ERROR, payload: "" });
      } else {
        dispatch({
          type: NOTIFICATION_ERROR,
          payload: getResponseMessage(response) || "Unable to load notifications.",
        });
      }

      return response;
    } finally {
      dispatch({ type: NOTIFICATION_LOADING, payload: false });
    }
  };

export const getUnreadNotificationCountAction = () => async (dispatch) => {
  const response = await notificationService.getUnreadCount();
  if (getResponseStatus(response) >= 200 && getResponseStatus(response) < 300) {
    const responseBody = response?.data || {};
    const data = responseBody?.data ?? responseBody;
    const countValue =
      typeof data === "number"
        ? data
        : getFirstDefined(
            data?.count,
            data?.unreadCount,
            data?.unreadNotificationCount,
            data?.unreadNotifications,
            data?.unread,
            data?.total,
            responseBody?.count,
            responseBody?.unreadNotificationCount,
            responseBody?.unreadNotifications,
            0,
          );
    dispatch({
      type: NOTIFICATION_COUNT,
      payload: Number(countValue) || 0,
    });
  }
  return response;
};

export const markNotificationReadAction =
  (id, { refreshCount = true } = {}) =>
  async (dispatch, getState) => {
    if (!id) return null;
    const notification = getState()?.notification?.items?.find(
      (item) => String(item.id) === String(id),
    );
    if (!notification || notification.read) return null;

    dispatch({ type: NOTIFICATION_READ, payload: String(id) });

    try {
      const response = await notificationService.markNotificationRead(id);
      if (getResponseStatus(response) >= 200 && getResponseStatus(response) < 300) {
        if (refreshCount) dispatch(getUnreadNotificationCountAction());
      } else {
        dispatch({ type: NOTIFICATION_READ_ROLLBACK, payload: String(id) });
        dispatch({
          type: NOTIFICATION_ERROR,
          payload: getResponseMessage(response) || "Unable to mark notification as read.",
        });
      }
      return response;
    } catch (error) {
      dispatch({ type: NOTIFICATION_READ_ROLLBACK, payload: String(id) });
      dispatch({
        type: NOTIFICATION_ERROR,
        payload: getResponseMessage(error) || "Unable to mark notification as read.",
      });
      return error;
    }
  };

export const markAllNotificationsReadAction = () => async (dispatch, getState) => {
  const unreadItems = (getState()?.notification?.items || []).filter(
    (item) => !item.read,
  );
  await Promise.all(
    unreadItems.map((item) =>
      dispatch(markNotificationReadAction(item.id, { refreshCount: false })),
    ),
  );
  dispatch(getUnreadNotificationCountAction());
};
